import { useState } from 'react';
import { Clock, Share2, Trash2 } from 'lucide-react';
import { toast } from '@reflex/ui/lib/toast';
import { resourceGrantAccessOf } from '@reflex/shared';
import {
  useResourceGrants,
  useRevokeResourceGrantMutation,
  useSetResourceGrantExpiryMutation,
  type ResourceGrant,
} from '@reflex/ui/client/api/resource-grants';
import type { OrgId } from '@reflex/ui/client/api/org-id';
import { CopyableId } from '@reflex/ui/components/CopyableId';
import { EmptyState } from '@reflex/ui/components/shared/EmptyState';
import { SectionCard } from '@reflex/ui/components/shared/SectionCard';
import { ConfirmDialog } from '@reflex/ui/components/shared/ConfirmDialog';
import { DataTablePagination } from '@reflex/ui/components/shared/DataTablePagination';
import { UserAvatar } from '@reflex/ui/components/shared/UserAvatar';
import { PageDescription, SectionLabel } from '@reflex/ui/components/layout/Typography';
import { Badge } from '@reflex/ui/components/ui/badge';
import { Button } from '@reflex/ui/components/ui/button';
import { Label } from '@reflex/ui/components/ui/label';
import { Switch } from '@reflex/ui/components/ui/switch';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '@reflex/ui/components/ui/dropdown-menu';
import { formatDateTime } from '@reflex/ui/lib/format';
import { cn } from '@reflex/ui/lib/utils';
import { PageLayout, PageHeader } from '@/components/layout/PageLayout';
import { ErrorState } from '@reflex/ui/components/shared/ErrorState';
import { ResourceChip } from '@/components/resource-preview/ResourceChip';
import { SubjectGlyph } from '@/components/sharing/SharePicker';
import {
  AGENT_SHARE_TYPE,
  groupSharesByKind,
  type ShareResourceKind,
  type ShareSection,
} from '@/components/sharing/share-resource-kinds';
import { useShareSubjects } from '@/components/sharing/useShareSubjects';
import { useActiveOrgId } from '@/hooks/auth/useActiveOrgId';
import { useAuth } from '@/hooks/auth/useAuth';
import { useAgent } from '@/hooks/agents/useAgents';

const DAY_MS = 86_400_000;

/** A user row's avatar source, the only field the subject icon needs. */
interface SubjectUser {
  id: string;
  avatarUrl: string | null;
}

/** Expiry timestamp for the quick-pick options; `null` never expires. */
function expiryFromDays(days: number | null): number | null {
  return days === null ? null : Date.now() + days * DAY_MS;
}

/** A share is expired once its interval has an exclusive end in the past. */
function isExpired(grant: ResourceGrant, now: number): boolean {
  return grant.expiresAt !== null && grant.expiresAt <= now;
}

/**
 * Sort order within one resource's shares, Notion-style: people and teams
 * alphabetically, the org-wide "everyone" share last (it is the general
 * access row of the group).
 */
function compareShares(labelOf: (g: ResourceGrant) => string) {
  return (a: ResourceGrant, b: ResourceGrant): number => {
    if ((a.subjectType === 'org') !== (b.subjectType === 'org'))
      return a.subjectType === 'org' ? 1 : -1;
    return labelOf(a).localeCompare(labelOf(b));
  };
}

/**
 * "Sharing" — the shared-resources surface, mounted in the Security drill at
 * `/{slug}/security/sharing` and backed by the `/resource-grants` API.
 *
 * A share gives a user, a team, or the whole organization access to one
 * resource until it expires or is revoked. The server already scopes the
 * list to what the caller may see — shares on resources they own plus
 * shares addressed to them, or the whole org for `roles:manage` holders —
 * and annotates each share with `canManage` (caller owns the shared
 * resource or is an org admin), which is exactly the predicate for showing
 * the extend/revoke controls.
 *
 * The page is one section per resource type (agent sessions, personas,
 * flows, …; see `share-resource-kinds.ts`), each split into the shares
 * addressed to the caller and the manageable shares grouped by resource.
 * Types with nothing shared get no section.
 */
export function SharingPage({ embedded = false }: { embedded?: boolean }) {
  const org = useActiveOrgId();
  if (!org) {
    const noOrg = (
      <>
        {!embedded && <SharingPageHeader />}
        <PageDescription>No active organization.</PageDescription>
      </>
    );
    if (embedded) return noOrg;
    return <PageLayout maxWidth="4xl">{noOrg}</PageLayout>;
  }
  return <SharingPageBody org={org} embedded={embedded} />;
}

function SharingPageHeader() {
  return (
    <PageHeader
      iconVariant="box"
      icon={<Share2 className="h-5 w-5 text-muted-foreground" />}
      title="Sharing"
      description="Review and manage your shared resources."
    />
  );
}

function SharingPageBody({ org, embedded = false }: { org: OrgId; embedded?: boolean }) {
  const { user, can } = useAuth();
  const canManageOrg = can('roles:manage');
  const [showExpired, setShowExpired] = useState(false);
  const [showAutomatic, setShowAutomatic] = useState(false);
  const [page, setPage] = useState(1);

  // The server bounds the read (grants are append-only history), so the page
  // is a window and `total` is the caller's whole set — never the org's.
  //
  // Deliberate shares only by default. An org whose "Default sharing for new
  // sessions" is not Private opens one `org-default` grant per session ever
  // created, which would bury the handful of shares somebody actually made
  // under thousands of automatic ones and make this page useless for the
  // review it exists for. The toggle is how you go and look at them.
  const grantsQuery = useResourceGrants(org, {
    page,
    ...(showExpired ? { includeEnded: 'true' } : {}),
    ...(showAutomatic ? {} : { source: 'member' as const }),
  });
  const { users, orgName, subjectLabel } = useShareSubjects();

  const revokeMutation = useRevokeResourceGrantMutation(org);
  const expiryMutation = useSetResourceGrantExpiryMutation(org);

  const grants = grantsQuery.data?.grants ?? [];
  // "Now" is the instant the list was last fetched (react-query's
  // `dataUpdatedAt`), so expired-ness re-evaluates on every refetch — a
  // freshly revoked share renders as expired, not as a live row. It also
  // keeps render pure (no bare `Date.now()` during render).
  const now = grantsQuery.dataUpdatedAt;
  const myUserId = user?.id ?? null;

  const sections = groupSharesByKind(grants, compareShares(subjectLabel));

  const revoke = (grant: ResourceGrant, successMessage: string) => {
    revokeMutation.mutate(
      { grantId: grant.id },
      {
        onSuccess: () => toast.success(successMessage),
        onError: (err) =>
          toast.error('Failed to stop sharing', {
            description: err instanceof Error ? err.message : undefined,
          }),
      },
    );
  };

  const setExpiry = (grant: ResourceGrant, days: number | null) => {
    expiryMutation.mutate(
      { grantId: grant.id, data: { expiresAt: expiryFromDays(days) } },
      {
        onSuccess: () =>
          toast.success(
            days === null ? 'Share set to never expire' : `Share expires in ${days} days`,
          ),
        onError: (err) =>
          toast.error('Failed to update the share', {
            description: err instanceof Error ? err.message : undefined,
          }),
      },
    );
  };

  const body = (
    <>
      {!embedded && <SharingPageHeader />}

      <div className="mb-4 flex items-center gap-2">
        <Switch
          id="show-expired-shares"
          checked={showExpired}
          // Ended shares change how many pages there are, so the window
          // restarts rather than landing the reader mid-history.
          onCheckedChange={(next) => {
            setShowExpired(next);
            setPage(1);
          }}
          aria-label="Show expired shares"
        />
        <Label htmlFor="show-expired-shares">Show expired shares</Label>
        <Switch
          id="show-automatic-shares"
          className="ml-4"
          checked={showAutomatic}
          // Same reasoning as expired shares: the set changes size, so the
          // window restarts instead of landing the reader mid-history.
          onCheckedChange={(next) => {
            setShowAutomatic(next);
            setPage(1);
          }}
          aria-label="Show automatic shares"
        />
        <Label htmlFor="show-automatic-shares">Show automatic shares</Label>
      </div>

      {grantsQuery.isLoading ? (
        <p className="text-sm text-muted-foreground">Loading shares…</p>
      ) : grantsQuery.isError ? (
        <ErrorState message="Failed to load shares." />
      ) : sections.length === 0 ? (
        <SectionCard
          title="Shared resources"
          contentClassName="py-0"
          className="gap-2 py-4 sm:gap-3"
          data-testid="sharing-empty"
        >
          <EmptyState
            title="Nothing is shared with you or by you right now."
            description="Share a session from the launch dialog or a session page. It shows up here under Agent sessions."
          />
        </SectionCard>
      ) : (
        <div className="space-y-6">
          {sections.map((section) => (
            <ShareTypeSection
              key={section.kind.type}
              section={section}
              canManageOrg={canManageOrg}
              orgName={orgName}
              now={now}
              users={users}
              myUserId={myUserId}
              busy={revokeMutation.isPending || expiryMutation.isPending}
              subjectLabel={subjectLabel}
              onRevoke={revoke}
              onSetExpiry={setExpiry}
            />
          ))}
          {grantsQuery.data && grantsQuery.data.totalPages > 1 ? (
            <DataTablePagination
              page={grantsQuery.data.page}
              pageSize={grantsQuery.data.pageSize}
              total={grantsQuery.data.total}
              onPageChange={setPage}
              itemLabel="share"
              isLoading={grantsQuery.isFetching}
            />
          ) : null}
        </div>
      )}
    </>
  );

  if (embedded) return body;

  return <PageLayout maxWidth="4xl">{body}</PageLayout>;
}

interface ShareTypeSectionProps {
  section: ShareSection;
  canManageOrg: boolean;
  orgName: string;
  /** Instant expiry is measured against (the list's fetch time). */
  now: number;
  users: SubjectUser[];
  myUserId: string | null;
  /** A share mutation is in flight, so the controls are disabled. */
  busy: boolean;
  subjectLabel: (grant: ResourceGrant) => string;
  onRevoke: (grant: ResourceGrant, successMessage: string) => void;
  onSetExpiry: (grant: ResourceGrant, days: number | null) => void;
}

/**
 * One resource type's shares: the ones addressed to the caller first, then
 * the ones they can manage, grouped by resource. Either half is omitted
 * when it is empty — the section only exists because something is shared.
 */
function ShareTypeSection({
  section,
  canManageOrg,
  orgName,
  now,
  users,
  myUserId,
  busy,
  subjectLabel,
  onRevoke,
  onSetExpiry,
}: ShareTypeSectionProps) {
  const { kind, sharedWithYou, managedGroups } = section;
  const Icon = kind.icon;

  return (
    <SectionCard
      title={kind.title}
      icon={<Icon aria-hidden="true" />}
      // No card description: the two sub-headings below already name what
      // each half holds, and repeating that per section is noise.
      contentClassName="space-y-4 py-0"
      className="gap-2 py-4 sm:gap-3"
      data-testid={`share-section-${kind.type}`}
    >
      {sharedWithYou.length > 0 ? (
        <div data-testid={`shared-with-you-${kind.type}`}>
          {/* Every section repeats these two headings, so each carries the
              type as screen-reader-only text: a flat heading list still
              tells "Shared with you" under Personas from the one under
              Agent sessions. */}
          <SectionLabel as="h3" tone="strong" className="mb-1.5">
            Shared with you<span className="sr-only"> ({kind.title})</span>
          </SectionLabel>
          <div className="divide-y">
            {sharedWithYou.map((grant) => (
              <SharedWithYouRow
                key={grant.id}
                grant={grant}
                kind={kind}
                expired={isExpired(grant, now)}
                label={subjectLabel(grant)}
                canDecline={
                  grant.subjectType === 'user' &&
                  grant.subjectId === myUserId &&
                  !isExpired(grant, now)
                }
                busy={busy}
                onDecline={() => onRevoke(grant, 'Share declined')}
              />
            ))}
          </div>
        </div>
      ) : null}

      {managedGroups.length > 0 ? (
        <div data-testid={`shared-by-you-${kind.type}`}>
          <SectionLabel as="h3" tone="strong" className="mb-1.5">
            {canManageOrg ? `All shares in ${orgName}` : 'Shared by you'}
            <span className="sr-only"> ({kind.title})</span>
          </SectionLabel>
          <div className="divide-y">
            {managedGroups.map((group) => (
              <div
                key={group[0].resourceId}
                className="py-3 first:pt-2 last:pb-2"
                data-testid={`share-group-${kind.type}-${group[0].resourceId}`}
              >
                <SharedResourceRef grant={group[0]} kind={kind} />
                <div className="mt-1.5 flex flex-col">
                  {group.map((grant) => (
                    <ManagedShareRow
                      key={grant.id}
                      grant={grant}
                      kind={kind}
                      expired={isExpired(grant, now)}
                      label={subjectLabel(grant)}
                      users={users}
                      busy={busy}
                      onRevoke={() => onRevoke(grant, 'Stopped sharing')}
                      onSetExpiry={(days) => onSetExpiry(grant, days)}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </SectionCard>
  );
}

/**
 * A share the caller receives. Declining (DELETE) is their only lever: the
 * server 403s them on /expire, and 403s decline on team/org subjects —
 * those are ended by whoever manages the share.
 */
function SharedWithYouRow({
  grant,
  kind,
  expired,
  label,
  canDecline,
  busy,
  onDecline,
}: {
  grant: ResourceGrant;
  kind: ShareResourceKind;
  expired: boolean;
  label: string;
  canDecline: boolean;
  busy: boolean;
  onDecline: () => void;
}) {
  return (
    <div
      className={cn('flex items-center justify-between gap-3 py-2.5', expired && 'opacity-60')}
      data-testid={`share-row-${grant.id}`}
    >
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex flex-wrap items-center gap-1.5">
          <SharedResourceRef grant={grant} kind={kind} />
          <AccessBadge roleId={grant.roleId} />
          {expired ? <Badge variant="subtle">Expired</Badge> : null}
        </div>
        <p className="truncate text-xs text-muted-foreground">
          {grant.subjectType === 'user' ? 'Shared directly with you' : `Shared with ${label}`}
          <span className="mx-1.5">·</span>
          {expiryLabel(grant, expired)}
        </p>
      </div>
      {canDecline ? (
        <Button
          variant="outline"
          size="sm"
          className="shrink-0"
          disabled={busy}
          // One "Decline" per shared resource, so the visible text alone
          // does not tell them apart out of context. Named by grant id.
          aria-label={`Decline share ${grant.id}`}
          onClick={onDecline}
        >
          Decline
        </Button>
      ) : null}
    </div>
  );
}

/** A share the caller may extend or stop, inside its resource's group. */
function ManagedShareRow({
  grant,
  kind,
  expired,
  label,
  users,
  busy,
  onRevoke,
  onSetExpiry,
}: {
  grant: ResourceGrant;
  kind: ShareResourceKind;
  expired: boolean;
  label: string;
  users: SubjectUser[];
  busy: boolean;
  onRevoke: () => void;
  onSetExpiry: (days: number | null) => void;
}) {
  return (
    <div
      className={cn('flex items-center gap-2.5 py-1.5', expired && 'opacity-60')}
      data-testid={`share-row-${grant.id}`}
    >
      <SubjectIcon grant={grant} users={users} label={label} />
      <div className="flex min-w-0 flex-1 flex-col">
        <span className="flex items-center gap-1.5 truncate text-sm">
          {label}
          {expired ? <Badge variant="subtle">Expired</Badge> : null}
        </span>
        <span className="truncate text-xs text-muted-foreground">
          {expiryLabel(grant, expired)}
        </span>
      </div>
      <AccessBadge roleId={grant.roleId} />
      {expired ? null : (
        <div className="flex shrink-0 items-center gap-1.5">
          <ExtendMenu grant={grant} label={label} disabled={busy} onPick={onSetExpiry} />
          <ConfirmDialog
            trigger={
              <Button
                variant="outline"
                size="sm"
                disabled={busy}
                aria-label={`Stop sharing with ${label}`}
              >
                <Trash2 className="icon-sm" />
              </Button>
            }
            title={`Stop sharing this ${kind.noun}?`}
            description={`${label} loses access to this ${kind.noun} immediately. The share moves to the expired list.`}
            confirmLabel="Stop sharing"
            onConfirm={onRevoke}
          />
        </div>
      )}
    </div>
  );
}

/**
 * The shared resource as the humans know it: a chip (icon, hover preview)
 * for any type with a registered resource preview. Only agents have a name
 * lookup here, so other types show their id until one exists; a type with
 * no preview at all falls back to its noun and a copyable id.
 */
function SharedResourceRef({ grant, kind }: { grant: ResourceGrant; kind: ShareResourceKind }) {
  if (grant.resourceType === AGENT_SHARE_TYPE && kind.previewKind)
    return <AgentRef agentId={grant.resourceId} previewKind={kind.previewKind} />;
  if (kind.previewKind) return <ResourceChip kind={kind.previewKind} id={grant.resourceId} />;
  return (
    <span className="flex items-center gap-1.5">
      <span className="text-sm font-medium capitalize">{kind.noun}</span>
      <CopyableId id={grant.resourceId} label={`Copy resource id ${grant.resourceId}`} />
    </span>
  );
}

/** An agent share, with the session title resolved when it is readable. */
function AgentRef({ agentId, previewKind }: { agentId: string; previewKind: string }) {
  const { data: agent } = useAgent(agentId);
  return <ResourceChip kind={previewKind} id={agentId} name={agent?.name} />;
}

function SubjectIcon({
  grant,
  users,
  label,
}: {
  grant: ResourceGrant;
  users: SubjectUser[];
  label: string;
}) {
  if (grant.subjectType === 'user') {
    const subject = users.find((u) => u.id === grant.subjectId);
    return <UserAvatar userName={label} avatarUrl={subject?.avatarUrl ?? undefined} />;
  }
  return <SubjectGlyph type={grant.subjectType} />;
}

/**
 * Expiry control for a live share. "Never expires" is the default and the
 * auto-selected state for an open-ended share; picking a window bounds it.
 */
function ExtendMenu({
  grant,
  label,
  disabled,
  onPick,
}: {
  grant: ResourceGrant;
  label: string;
  disabled: boolean;
  onPick: (days: number | null) => void;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          disabled={disabled}
          aria-label={`Set expiry for share with ${label}`}
        >
          <Clock className="icon-sm" />
          Extend
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuRadioGroup
          // A bounded share intentionally checks nothing: its instant does
          // not map back onto the quick-pick windows.
          value={grant.expiresAt === null ? 'never' : 'dated'}
          onValueChange={(next) => {
            // Re-picking the already-checked "Never expires" is a no-op, not
            // a redundant mutation and a misleading success toast.
            if (next === 'never' && grant.expiresAt === null) return;
            onPick(next === 'never' ? null : Number(next));
          }}
        >
          <DropdownMenuRadioItem value="never">Never expires</DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="7">For 7 days</DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="30">For 30 days</DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="90">For 90 days</DropdownMenuRadioItem>
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** "Can view" / "Can edit" for the two sharing roles; raw role id otherwise. */
function AccessBadge({ roleId }: { roleId: string }) {
  const access = resourceGrantAccessOf(roleId);
  if (access === 'view') return <Badge variant="secondary">Can view</Badge>;
  if (access === 'edit') return <Badge variant="secondary">Can edit</Badge>;
  return (
    <Badge variant="outline" className="font-mono">
      {roleId}
    </Badge>
  );
}

function expiryLabel(grant: ResourceGrant, expired: boolean): string {
  if (grant.expiresAt === null) return 'Never expires';
  if (expired) return `Expired ${formatDateTime(grant.expiresAt)}`;
  return `Expires ${formatDateTime(grant.expiresAt)}`;
}
